import { FC, useState } from "react";
import { createPortal } from "react-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useData } from "data/data-provider";
import toast from "react-hot-toast";

export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  onClick?: any;
  clickFunction: any;
  loading?: boolean;
}

const FleetCard: FC<FleetCardProps> = ({
  size = "default",
  className = "",
  data,
  simpleImg = false,
  onClick,
  clickFunction,
  loading,
}) => {
  const { setSelectedCar, selectedCar } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const {
    id,
    fleet_photos,
    name,
    color,
    description,
    seats,
    avg_car_price_per_day,
    thumbnail_photo_url,
  } = data!;

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleMouseLeave = () => setIsHovered(false);

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border ${
        selectedCar && selectedCar.id === id
          ? "border-primary-100"
          : "border-neutral-100 dark:border-neutral-800"
      } rounded-2xl overflow-hidden hover:shadow-xl transition-shadow ${className}`}
      data-nc-id="StayCard"
      onClick={onClick}
    >
      {/* Fleet Image */}
      <img
        src={
          fleet_photos && fleet_photos.length > 0
            ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${thumbnail_photo_url}`
            : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
        }
        alt="name"
        className="h-[200px] object-cover w-full"
      />

      {/* Fleet Info */}
      <div className={size === "default" ? "p-4 space-y-4" : "p-3 space-y-2"}>
        <h2
          className={`font-medium capitalize ${
            size === "default" ? "text-lg" : "text-base"
          }`}
        >
          <p className="truncate w-full max-w-full">{name}</p>
        </h2>

        {/* Description with Tooltip */}
        <div className="relative group">
          <span
            className="block line-clamp-2 truncate cursor-pointer relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {description}
          </span>
        </div>

        {/* Tooltip (Using React Portal to prevent clipping) */}
        {isHovered &&
          createPortal(
            <div
              className="absolute bg-[#fbfaeb] text-black text-xs rounded-md px-2 py-1 shadow-lg z-50"
              style={{
                top: tooltipPosition.top + 24,
                left: tooltipPosition.left,
                maxWidth: "250px", // Adjust width as needed
                whiteSpace: "normal", // Allows wrapping
                wordWrap: "break-word", // Ensures text breaks properly
              }}
            >
              {description}
            </div>,
            document.body
          )}

        {/* Fleet Details */}
        <span className="text-sm">
          <span className="font-semibold">{seats}</span> seats{" "}
          <span className="font-semibold">{color}</span> color
        </span>

        {/* Pricing and Select Button */}
        <div className="w-14 border-b border-neutral-100 dark:border-neutral-800"></div>
        <div className="flex justify-between items-center">
          <span className="text-base font-semibold">
            {avg_car_price_per_day ? `$${avg_car_price_per_day}` : "$0"}
            {size === "default" && (
              <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                {" "}
                /day
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FleetCard;
